import { Component } from '@theme/component';

class TabContent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  #init() {
    // Shopify's Blocks API wraps each block in a <div>. Find direct children
    // (the wrappers) that contain a [data-tab-title] descendant.
    /** @type {{ wrapper: HTMLElement, title: string, url: string }[]} */
    const wrapperPanels = [];
    for (const wrapper of this.children) {
      const panel = wrapper.matches('[data-tab-title]')
        ? wrapper
        : wrapper.querySelector('[data-tab-title]');
      if (panel instanceof HTMLElement && panel.dataset.tabTitle) {
        wrapperPanels.push({
          wrapper: /** @type {HTMLElement} */ (wrapper),
          title: panel.dataset.tabTitle,
          url: panel.dataset.collectionUrl ?? '',
        });
      }
    }

    /** @type {HTMLAnchorElement | null} */
    const generalButton = this.querySelector('[data-tab-general-button] a');
    const urls = wrapperPanels.map((d) => d.url);

    if (generalButton && wrapperPanels[0]) this.#updateGeneralButton(generalButton, wrapperPanels[0].url);

    if (wrapperPanels.length < 2) return;

    const nav = document.createElement('div');
    nav.className = 'cc-tab-nav';
    nav.setAttribute('role', 'tablist');

    const wrappers = wrapperPanels.map((d) => d.wrapper);

    wrapperPanels.forEach(({ wrapper, title }, i) => {
      wrapper.setAttribute('role', 'tabpanel');
      if (i !== 0) wrapper.classList.add('hidden');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-tab-btn';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.textContent = title;
      btn.addEventListener('click', () => this.#activate(wrappers, i, urls, generalButton));
      nav.append(btn);
    });

    this.prepend(nav);
    this.#enableDragScroll(nav);

    // When placement is "inline", pull the general button ("View all") up onto
    // the tab-nav row so it aligns with the tabs. Otherwise leave it where the
    // template rendered it (below the tab content).
    const generalButtonWrapper = this.querySelector('[data-tab-general-button]');
    const placement = this.dataset.generalButtonPlacement ?? 'inline';
    if (generalButtonWrapper && placement !== 'below') {
      const navRow = document.createElement('div');
      navRow.className = 'cc-tab-nav-row';
      nav.replaceWith(navRow);
      navRow.append(nav, generalButtonWrapper);
    }
  }

  /**
   * Enables click-and-drag horizontal scrolling on desktop (mouse only - touch
   * devices already get native drag-scroll from `overflow-x`/`touch-action`
   * in the CSS, and trackpad/touch pointers shouldn't fight that here).
   * @param {HTMLElement} nav
   */
  #enableDragScroll(nav) {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;

    nav.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || nav.scrollWidth <= nav.clientWidth) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = nav.scrollLeft;
      nav.classList.add('is-dragging');
    });

    nav.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 3) moved = true;
      nav.scrollLeft = startScrollLeft - delta;
    });

    const stopDragging = () => {
      dragging = false;
      nav.classList.remove('is-dragging');
    };
    nav.addEventListener('pointerup', stopDragging);
    nav.addEventListener('pointerleave', stopDragging);
    nav.addEventListener('pointercancel', stopDragging);

    // Swallow the click a drag release would otherwise fire on a tab button.
    nav.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /**
   * @param {Element[]} panels
   * @param {number} activeIndex
   * @param {string[]} urls
   * @param {HTMLAnchorElement | null} generalButton
   */
  #activate(panels, activeIndex, urls, generalButton) {
    const btns = [...this.querySelectorAll('.cc-tab-btn')];
    panels.forEach((panel, i) => {
      const isActive = i === activeIndex;
      btns[i]?.setAttribute('aria-selected', isActive ? 'true' : 'false');
      panel.classList.toggle('hidden', !isActive);
    });
    // Keep the selected tab in view when the nav is horizontally scrollable.
    btns[activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (generalButton) this.#updateGeneralButton(generalButton, urls[activeIndex]);
  }

  /**
   * @param {HTMLAnchorElement} anchor
   * @param {string} [url]
   */
  #updateGeneralButton(anchor, url) {
    if (!url) {
      anchor.removeAttribute('href');
      anchor.setAttribute('role', 'link');
      anchor.setAttribute('aria-disabled', 'true');
      return;
    }
    anchor.href = url;
    anchor.removeAttribute('role');
    anchor.removeAttribute('aria-disabled');
  }
}

if (!customElements.get('tab-content')) {
  customElements.define('tab-content', TabContent);
}
